import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { 
  CreditCard, Shield, Clock, Check, AlertCircle, 
  Loader2, ArrowLeft, Building2, FileCheck, Crown
} from 'lucide-react';
import axios from 'axios';
import SEO from '../components/shared/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Tier icons mapping
const TIER_ICONS = {
  foundation: Building2,
  accelerator: FileCheck,
  elite: Crown
};

// Tier colors mapping
const TIER_COLORS = {
  foundation: '#CD7F32',
  accelerator: '#C0C0C0',
  elite: '#D4AF37'
};

const StarterPackCheckoutPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const appId = searchParams.get('app_id');
  
  const [application, setApplication] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!appId) {
      setError('No application ID provided');
      setIsLoading(false);
      return;
    }
    fetchApplication();
  }, [appId]);

  const fetchApplication = async () => {
    try {
      const response = await axios.get(`${API}/starter-pack/application/${appId}`);
      
      if (response.data.is_expired) {
        setError('This checkout link has expired. Please submit a new application.');
        setApplication(null);
      } else {
        setApplication(response.data);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load application');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCheckout = async () => {
    if (!application) return;
    
    setIsProcessing(true);
    setError(null);
    
    try {
      const response = await axios.post(`${API}/starter-pack/checkout/create`, {
        application_id: appId,
        origin_url: window.location.origin
      });
      
      // Redirect to Stripe checkout
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create checkout session');
      setIsProcessing(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 text-[#D4AF37] animate-spin mx-auto mb-4" />
          <p className="text-white/60">Loading checkout...</p>
        </div>
      </div>
    );
  }

  if (error && !application) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="max-w-md mx-auto px-6 text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Checkout Error</h1>
          <p className="text-white/60 mb-6">{error}</p>
          <button
            onClick={() => navigate('/build/apply')}
            className="bg-[#D4AF37] text-black px-6 py-3 font-bold hover:bg-[#F5D47B] transition-colors"
            data-testid="reapply-btn"
          >
            Submit New Application
          </button>
        </div>
      </div>
    );
  }

  const tier = application?.selected_tier || 'foundation';
  const tierInfo = application?.tier_info || {};
  const TierIcon = TIER_ICONS[tier] || Building2;
  const tierColor = TIER_COLORS[tier] || '#D4AF37';

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="starter-pack-checkout-page">
      <SEO 
        title="Checkout - Signature Program | Queen B Logistics"
        description="Complete your Signature Program purchase and begin building your trucking business."
        canonical="https://queenblogistics.com/build/checkout"
      />
      
      {/* Header */}
      <div className="bg-[#0D0D0D] border-b border-white/10 py-6">
        <div className="max-w-2xl mx-auto px-6">
          <button
            onClick={() => navigate('/build')}
            className="flex items-center gap-2 text-white/60 hover:text-white mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Signature Program
          </button>
          <h1 className="font-['Barlow_Condensed'] text-3xl font-bold text-white uppercase">
            Complete Your <span className="text-[#D4AF37]">Purchase</span>
          </h1>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-2xl mx-auto px-6 py-8">
        {/* Order Summary */}
        <div className="bg-[#121212] border border-white/10 p-6 mb-6">
          <h2 className="font-['Barlow_Condensed'] text-lg font-bold text-[#D4AF37] uppercase mb-4 flex items-center gap-2">
            <CreditCard className="w-5 h-5" />
            Order Summary
          </h2>
          
          <div className="flex items-start gap-4 p-4 bg-[#0A0A0A] border border-white/5">
            <div 
              className="w-12 h-12 flex items-center justify-center"
              style={{ backgroundColor: `${tierColor}20`, border: `1px solid ${tierColor}` }}
            >
              <TierIcon className="w-6 h-6" style={{ color: tierColor }} />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span 
                  className="px-2 py-0.5 text-xs font-bold uppercase"
                  style={{ backgroundColor: `${tierColor}20`, color: tierColor }}
                >
                  {tierInfo.name || 'Foundation'}
                </span>
                <span className="text-white/40 text-sm">Signature Program</span>
              </div>
              <p className="text-white font-medium mt-1">{tierInfo.description || 'Business Foundation Package'}</p>
              <p className="text-white/50 text-sm mt-1">One-time payment • No recurring fees</p>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-white">${tierInfo.price?.toLocaleString() || '997'}</p>
            </div>
          </div>
        </div>

        {/* Customer Info */}
        <div className="bg-[#121212] border border-white/10 p-6 mb-6">
          <h2 className="font-['Barlow_Condensed'] text-lg font-bold text-white uppercase mb-4">
            Customer Information
          </h2>
          
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-white/50">Name</p>
              <p className="text-white">{application?.full_name}</p>
            </div>
            <div>
              <p className="text-white/50">Email</p>
              <p className="text-white">{application?.email}</p>
            </div>
            <div className="col-span-2">
              <p className="text-white/50">Company</p>
              <p className="text-white">{application?.company_name}</p>
            </div>
          </div>
        </div>

        {/* What Happens Next */}
        <div className="bg-[#121212] border border-white/10 p-6 mb-6">
          <h2 className="font-['Barlow_Condensed'] text-lg font-bold text-white uppercase mb-4">
            What Happens Next
          </h2>
          
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-[#D4AF37]/10 flex items-center justify-center mt-0.5">
                <span className="text-[#D4AF37] text-xs font-bold">1</span>
              </div>
              <div>
                <p className="text-white font-medium">Complete Payment</p>
                <p className="text-white/50 text-sm">Secure checkout powered by Stripe</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-[#D4AF37]/10 flex items-center justify-center mt-0.5">
                <span className="text-[#D4AF37] text-xs font-bold">2</span>
              </div>
              <div>
                <p className="text-white font-medium">Receive Welcome Email</p>
                <p className="text-white/50 text-sm">Instructions to set up your Hive account</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-[#D4AF37]/10 flex items-center justify-center mt-0.5">
                <span className="text-[#D4AF37] text-xs font-bold">3</span>
              </div>
              <div>
                <p className="text-white font-medium">Begin Phase 1 - Intake</p>
                <p className="text-white/50 text-sm">Start your 5-phase business launch journey</p>
              </div>
            </div>
          </div>
        </div>

        {/* Security & Guarantees */}
        <div className="bg-[#121212] border border-white/10 p-6 mb-6">
          <div className="flex items-center gap-6 text-sm">
            <div className="flex items-center gap-2 text-white/60">
              <Shield className="w-4 h-4 text-green-500" />
              <span>Secure Payment</span>
            </div>
            <div className="flex items-center gap-2 text-white/60">
              <Clock className="w-4 h-4 text-[#D4AF37]" />
              <span>Instant Access</span>
            </div>
            <div className="flex items-center gap-2 text-white/60">
              <Check className="w-4 h-4 text-blue-500" />
              <span>No Hidden Fees</span>
            </div>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/30 p-4 mb-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
              <p className="text-red-400">{error}</p>
            </div>
          </div>
        )}

        {/* Checkout Button */}
        <button
          onClick={handleCheckout}
          disabled={isProcessing}
          className={`w-full py-4 text-lg font-bold uppercase tracking-wider transition-all flex items-center justify-center gap-3 ${
            isProcessing 
              ? 'bg-zinc-700 text-zinc-400 cursor-not-allowed'
              : 'bg-[#D4AF37] text-black hover:bg-[#F5D47B]'
          }`}
          data-testid="proceed-to-payment-btn"
        >
          {isProcessing ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Redirecting to Stripe...
            </>
          ) : (
            <>
              <CreditCard className="w-5 h-5" />
              Proceed to Payment - ${tierInfo.price?.toLocaleString() || '997'}
            </>
          )}
        </button>

        {/* Disclaimer */}
        <p className="text-white/30 text-xs text-center mt-6">
          By completing this purchase, you agree to our Terms of Service. 
          Queen B Logistics provides structured guidance only. Government filings remain your responsibility.
        </p>
      </div>
    </div>
  );
};

export default StarterPackCheckoutPage;
